/**
 * SportsOrganizerPage — PIN-gated event desk for outside organizers (Phase 4).
 *
 * /sports/organizer/:tid/:token — works for BOTH tournaments (teams) and open
 * play sessions (players); the link itself knows which event it belongs to.
 * Marshal unlocks with the event PIN + their name; every collection is recorded
 * under that name in the event's own cashbox. Serves both styles:
 *   - informal: tap row → "Collect ₱X" → confirm → done
 *   - meticulous: Details → custom amount, method, cash change, waive with
 *     reason, payment history, who's checked in
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { fmtDate } from '../utils/dates';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PAY_COLORS = {
  paid: '#00E676', free: '#737373', deposit_paid: '#FFB300',
  partial: '#FFB300', pending: '#E63946', at_venue: '#E63946',
  awaiting_payment: '#8B5CF6',
};

export default function SportsOrganizerPage() {
  const { tid, token } = useParams(); // tid = event id (tournament OR open play session)
  const storageKey = `sports_organizer_${tid}`;

  const [session, setSession] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey)) || null; } catch { return null; }
  });
  const [pin, setPin] = useState('');
  const [marshal, setMarshal] = useState('');
  const [unlockErr, setUnlockErr] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [state, setState] = useState(null);
  const [q, setQ] = useState('');
  const [openRow, setOpenRow] = useState(null);   // participant id expanded
  const [busy, setBusy] = useState(false);
  const [confirmCollect, setConfirmCollect] = useState(null); // {p, amount, method, tendered}
  const [detailMode, setDetailMode] = useState(false);
  const [amt, setAmt] = useState('');
  const [method, setMethod] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [waiveMode, setWaiveMode] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [flash, setFlash] = useState('');
  const fileInputRef = useRef(null);

  const H = session ? { Authorization: `Bearer ${session.session_token}` } : null;
  const isOpenPlay = state?.event?.kind === 'open_play' || session?.event?.kind === 'open_play';

  const loadState = useCallback(async (tok = session?.session_token) => {
    if (!tok) return;
    try {
      const r = await axios.get(`${BACKEND}/api/sports/organizer/state`,
        { headers: { Authorization: `Bearer ${tok}` } });
      setState(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(storageKey);
        setSession(null);
      }
    }
  }, [session, storageKey]);

  useEffect(() => { loadState(); /* eslint-disable-next-line */ }, [session?.session_token]);

  const unlock = async () => {
    setUnlocking(true); setUnlockErr('');
    try {
      const r = await axios.post(`${BACKEND}/api/sports/public/organizer/${tid}/${token}/unlock`,
        { pin: pin.trim(), marshal_name: marshal.trim() });
      sessionStorage.setItem(storageKey, JSON.stringify(r.data));
      setSession(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setUnlockErr((d && (d.message || d)) || 'Could not unlock.');
    } finally {
      setUnlocking(false);
    }
  };

  const doCollect = async (p, amount, m, tnd) => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/sports/organizer/participants/${p.id}/collect`,
        { amount, method: m, tendered: tnd }, { headers: H });
      setFlash(`₱${Number(amount).toLocaleString()} from ${p.who} recorded`
        + (r.data.change > 0 ? ` — give ₱${r.data.change.toLocaleString()} change` : ''));
      setConfirmCollect(null); setDetailMode(false); setAmt(''); setTendered('');
      await loadState();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert((d && (d.message || d)) || 'Could not record the payment.');
    } finally {
      setBusy(false);
    }
  };

  const doWaive = async (p) => {
    if (waiveReason.trim().length < 3) { alert('Enter a reason for the waiver.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/sports/organizer/participants/${p.id}/waive`,
        { reason: waiveReason.trim() }, { headers: H });
      setFlash(`Fee waived for ${p.who} (₱${Number(r.data.waived).toLocaleString()})`);
      setWaiveMode(false); setWaiveReason('');
      await loadState();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert((d && (d.message || d)) || 'Could not waive.');
    } finally {
      setBusy(false);
    }
  };

  const doCheckIn = async (p) => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/sports/organizer/participants/${p.id}/check-in`, {}, { headers: H });
      setFlash(`${p.who} checked in`);
      await loadState();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert((d && (d.message || d)) || 'Check-in failed.');
    } finally {
      setBusy(false);
    }
  };

  // ── QR upload (works without HTTPS/camera) ────────────────────────────
  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    let decoded = null;
    if ('BarcodeDetector' in window) {
      try {
        const codes = await new window.BarcodeDetector({ formats: ['qr_code'] })
          .detect(await createImageBitmap(file));
        if (codes.length) decoded = codes[0].rawValue;
      } catch (_) {}
    }
    if (!decoded) {
      const tempId = `org-qr-${Date.now()}`;
      const el = document.createElement('div');
      el.id = tempId;
      el.style.cssText = 'position:fixed;top:0;left:0;z-index:-9999;width:300px;height:300px;opacity:0;pointer-events:none;overflow:hidden;';
      document.body.appendChild(el);
      try {
        const qr = new Html5Qrcode(tempId);
        decoded = await qr.scanFile(file, true);
        try { qr.clear(); } catch (_) {}
      } catch (_) {
        alert('No QR code found in this image.');
      } finally {
        document.getElementById(tempId)?.remove();
      }
    }
    if (!decoded) return;
    try {
      const r = await axios.post(`${BACKEND}/api/sports/organizer/resolve`,
        { code: decoded }, { headers: H });
      if (!r.data?.found) { alert(r.data?.reason || 'Not recognized.'); return; }
      setOpenRow(r.data.id); setDetailMode(false); setWaiveMode(false);
      setFlash(`Scanned: ${r.data.who}`);
    } catch {
      alert('Could not resolve this QR.');
    }
  };

  const inp = { width: '100%', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 10, padding: '12px 14px', fontSize: 16, outline: 'none', boxSizing: 'border-box' };

  // ── Unlock screen ───────────────────────────────────────────────────────
  if (!session) return (
    <div style={{ minHeight: '100dvh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center' }}>
          Organizer <span style={{ color: '#E63946' }}>Desk</span>
        </p>
        <p style={{ fontSize: 13, color: '#737373', textAlign: 'center', margin: '6px 0 22px' }}>
          Enter the event PIN and your name. Every payment you take is recorded under your name.
        </p>
        <p style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Your name</p>
        <input data-testid="org-marshal-input" value={marshal} onChange={e => setMarshal(e.target.value)} placeholder="e.g. Marco" style={{ ...inp, marginBottom: 14 }} />
        <p style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Event PIN</p>
        <input data-testid="org-pin-input" value={pin} onChange={e => setPin(e.target.value)} inputMode="numeric" placeholder="••••" style={{ ...inp, marginBottom: 14, letterSpacing: 6, fontWeight: 800 }} />
        {unlockErr && <p style={{ fontSize: 12, color: '#FF2D55', marginBottom: 10 }}>{unlockErr}</p>}
        <button data-testid="org-unlock-btn" onClick={unlock} disabled={unlocking || !pin.trim() || !marshal.trim()}
          style={{ width: '100%', padding: 14, borderRadius: 12, background: '#E63946', border: 'none', color: '#fff', fontWeight: 900, fontSize: 15, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
          {unlocking ? 'Unlocking…' : 'Unlock'}
        </button>
      </div>
    </div>
  );

  const parts = (state?.participants || []).filter(p =>
    !q || (p.who || '').toLowerCase().includes(q.toLowerCase())
      || (p.members || []).some(m => (m || '').toLowerCase().includes(q.toLowerCase()))
      || (p.phone || '').includes(q));
  const st = state?.settlement;

  return (
    <div style={{ minHeight: '100dvh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif', maxWidth: 520, margin: '0 auto', padding: '16px 16px 60px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.15 }}>{state?.event?.name || 'Event'}</p>
          <p style={{ fontSize: 11, color: '#737373' }}>
            {isOpenPlay ? 'Open play' : 'Tournament'} · {fmtDate(state?.event?.date || '')} · marshal: <b style={{ color: '#E63946' }}>{session.marshal}</b>
          </p>
        </div>
        <button onClick={() => { sessionStorage.removeItem(storageKey); setSession(null); setState(null); }}
          style={{ background: 'transparent', border: '1px solid #2A2A2A', color: '#737373', borderRadius: 8, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>
          Lock
        </button>
      </div>

      {/* Settlement strip */}
      {st && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, marginBottom: 12 }} data-testid="org-settlement-strip">
          {[['Collectable', st.collectable, '#fff'], ['Collected', st.collected, '#00E676'],
            ['Owed', st.outstanding_total, '#FF2D55'], ['Comped', st.comped, '#FFB300']].map(([k, v, c]) => (
            <div key={k} style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 10, padding: '8px 10px' }}>
              <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: '#555' }}>{k}</p>
              <p style={{ fontSize: 14, fontWeight: 900, color: c }}>₱{Number(v || 0).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {flash && (
        <div style={{ background: '#00E67614', border: '1px solid #00E67640', borderRadius: 10, padding: '9px 13px', marginBottom: 10 }}>
          <p style={{ fontSize: 12.5, color: '#00E676', fontWeight: 700 }}>✓ {flash}</p>
        </div>
      )}

      {/* Search + QR upload */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={isOpenPlay ? 'Search player or phone…' : 'Search team or player…'}
          style={{ ...inp, padding: '10px 12px', fontSize: 14 }} />
        <button onClick={() => fileInputRef.current?.click()} title="Scan QR from a photo/screenshot"
          style={{ flexShrink: 0, background: '#E6394614', border: '1px solid #E6394640', color: '#E63946', borderRadius: 10, padding: '0 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
          QR
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleQrUpload} />
      </div>

      {/* Participant list */}
      {parts.map(p => {
        const open = openRow === p.id;
        const bal = p.money?.balance_due || 0;
        const pc = PAY_COLORS[p.payment_status] || '#737373';
        return (
          <div key={p.id} data-testid={`org-part-${p.id}`}
            style={{ background: '#141414', border: `1px solid ${open ? '#E6394640' : '#1E1E1E'}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
            <div onClick={() => { setOpenRow(open ? null : p.id); setDetailMode(false); setWaiveMode(false); setAmt(''); setTendered(''); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 900, textTransform: 'uppercase' }}>
                  {p.checked_in ? '🟢 ' : ''}{p.who}
                </p>
                <p style={{ fontSize: 10.5, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {(p.members || []).join(', ') || p.phone || (isOpenPlay ? (p.player_status || '') : '')}
                </p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ fontSize: 9.5, padding: '3px 8px', borderRadius: 5, fontWeight: 800, textTransform: 'uppercase', background: pc + '18', color: pc }}>
                  {(p.payment_status || '?').replace('_', ' ')}
                </span>
                {bal > 0 && <p style={{ fontSize: 12, color: '#FF2D55', fontWeight: 800, marginTop: 3 }}>owes ₱{Number(bal).toLocaleString()}</p>}
              </div>
            </div>

            {open && (
              <div style={{ marginTop: 12, borderTop: '1px solid #1E1E1E', paddingTop: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
                  {[['Due', p.money.amount_due], ['Paid', p.money.amount_paid],
                    ['Waived', p.money.waived_amount], ['Balance', p.money.balance_due]].map(([k, v]) => (
                    <div key={k} style={{ background: '#0A0A0A', borderRadius: 7, padding: '6px 8px' }}>
                      <p style={{ fontSize: 8.5, textTransform: 'uppercase', color: '#555' }}>{k}</p>
                      <p style={{ fontSize: 12, fontWeight: 800, color: k === 'Balance' && v > 0 ? '#FF2D55' : '#ddd' }}>₱{Number(v || 0).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                {p.proof_key && <p style={{ fontSize: 11, color: '#8B5CF6', marginBottom: 8 }}>📎 Payment proof was uploaded — verify in the owner dashboard.</p>}

                {!detailMode && !waiveMode && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {bal > 0 && (
                      <button data-testid={`org-quick-collect-${p.id}`} disabled={busy}
                        onClick={() => setConfirmCollect({ p, amount: bal, method: 'cash', tendered: null })}
                        style={{ flex: 2, minWidth: 130, padding: 11, borderRadius: 9, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, fontSize: 12.5, textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Oswald, sans-serif' }}>
                        Collect ₱{Number(bal).toLocaleString()}
                      </button>
                    )}
                    {bal > 0 && (
                      <button onClick={() => { setDetailMode(true); setAmt(String(bal)); }} disabled={busy}
                        style={{ flex: 1, minWidth: 80, padding: 11, borderRadius: 9, background: '#141414', border: '1px solid #2A2A2A', color: '#A3A3A3', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', cursor: 'pointer' }}>
                        Details
                      </button>
                    )}
                    {!p.checked_in && (
                      <button data-testid={`org-checkin-${p.id}`} onClick={() => doCheckIn(p)} disabled={busy}
                        style={{ flex: 1, minWidth: 90, padding: 11, borderRadius: 9, background: bal > 0 ? '#141414' : '#00E67618', border: `1px solid ${bal > 0 ? '#2A2A2A' : '#00E67650'}`, color: bal > 0 ? '#737373' : '#00E676', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', cursor: 'pointer' }}>
                        Check in
                      </button>
                    )}
                    {bal > 0 && (
                      <button onClick={() => setWaiveMode(true)} disabled={busy}
                        style={{ flexBasis: '100%', padding: 8, borderRadius: 9, background: 'transparent', border: '1px dashed #2A2A2A', color: '#555', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                        Waive fee (invited {isOpenPlay ? 'player' : 'team'} / promo)
                      </button>
                    )}
                  </div>
                )}

                {detailMode && (
                  <div>
                    <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', marginBottom: 4 }}>Amount received</p>
                    <input type="number" inputMode="decimal" value={amt} onChange={e => setAmt(e.target.value)} style={{ ...inp, padding: '9px 12px', fontSize: 15, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                      {['cash', 'gcash', 'bank'].map(m => (
                        <button key={m} onClick={() => setMethod(m)}
                          style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `1.5px solid ${method === m ? '#00E676' : '#2A2A2A'}`, background: method === m ? '#00E67614' : '#0A0A0A', color: method === m ? '#00E676' : '#737373', fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', cursor: 'pointer' }}>
                          {m}
                        </button>
                      ))}
                    </div>
                    {method === 'cash' && (
                      <>
                        <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', marginBottom: 4 }}>Cash handed over (optional)</p>
                        <input type="number" inputMode="decimal" value={tendered} onChange={e => setTendered(e.target.value)} placeholder="for change" style={{ ...inp, padding: '9px 12px', fontSize: 15, marginBottom: 6 }} />
                        {tendered && parseFloat(tendered) >= (parseFloat(amt) || 0) && (
                          <p style={{ fontSize: 12.5, color: '#FFB300', fontWeight: 800, marginBottom: 6 }}>
                            Change: ₱{(parseFloat(tendered) - (parseFloat(amt) || 0)).toLocaleString()}
                          </p>
                        )}
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setDetailMode(false)} style={{ flex: 1, padding: 10, borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', cursor: 'pointer' }}>Back</button>
                      <button disabled={busy || !(parseFloat(amt) > 0)}
                        onClick={() => setConfirmCollect({ p, amount: Math.min(parseFloat(amt) || 0, bal), method, tendered: tendered ? parseFloat(tendered) : null })}
                        style={{ flex: 2, padding: 10, borderRadius: 8, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, fontSize: 11.5, textTransform: 'uppercase', cursor: 'pointer' }}>
                        Record payment
                      </button>
                    </div>
                    {(p.payment_history || []).length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <p style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', marginBottom: 4 }}>History</p>
                        {(p.payment_history || []).slice().reverse().map(h => (
                          <p key={h.id} style={{ fontSize: 11, color: '#737373', margin: '2px 0' }}>
                            {h.amount > 0 ? `₱${Number(h.amount).toLocaleString()} · ${h.method}` : h.note} — {h.recorded_by} · {(h.recorded_at || '').slice(0, 16).replace('T', ' ')}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {waiveMode && (
                  <div>
                    <p style={{ fontSize: 11, color: '#FFB300', marginBottom: 6 }}>
                      Waive the remaining ₱{Number(bal).toLocaleString()}. A reason is required — it appears in the settlement report.
                    </p>
                    <input data-testid={`org-waive-reason-${p.id}`} value={waiveReason} onChange={e => setWaiveReason(e.target.value)}
                      placeholder="e.g. invited player — makes the event better" style={{ ...inp, padding: '9px 12px', fontSize: 14, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => { setWaiveMode(false); setWaiveReason(''); }} style={{ flex: 1, padding: 10, borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', cursor: 'pointer' }}>Back</button>
                      <button data-testid={`org-waive-confirm-${p.id}`} disabled={busy} onClick={() => doWaive(p)}
                        style={{ flex: 2, padding: 10, borderRadius: 8, background: '#FFB300', border: 'none', color: '#000', fontWeight: 900, fontSize: 11.5, textTransform: 'uppercase', cursor: 'pointer' }}>
                        Waive ₱{Number(bal).toLocaleString()}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {parts.length === 0 && <p style={{ fontSize: 13, color: '#555', textAlign: 'center', padding: 24 }}>No {isOpenPlay ? 'players' : 'teams'} found.</p>}

      {/* Per-collector settlement */}
      {st && Object.keys(st.by_collector || {}).length > 0 && (
        <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 14, marginTop: 16 }}>
          <p style={{ fontSize: 11, color: '#737373', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Collected — by person</p>
          {Object.entries(st.by_collector).sort((a, b) => b[1].amount - a[1].amount).map(([who, v]) => (
            <div key={who} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <p style={{ fontSize: 12.5, color: '#A3A3A3' }}>{who.replace('organizer:', '')}</p>
              <p style={{ fontSize: 12.5, color: '#00E676', fontWeight: 800 }}>₱{Number(v.amount).toLocaleString()} <span style={{ color: '#555', fontWeight: 400 }}>({v.count})</span></p>
            </div>
          ))}
        </div>
      )}

      {/* Double-confirm dialog */}
      {confirmCollect && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#141414', border: '1px solid #00E67650', borderRadius: 16, padding: 22, width: '100%', maxWidth: 340 }}>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 900, color: '#00E676', textTransform: 'uppercase', marginBottom: 10 }}>Confirm payment</p>
            <p style={{ fontSize: 14, color: '#ccc', marginBottom: 4 }}>
              <b style={{ color: '#fff' }}>₱{Number(confirmCollect.amount).toLocaleString()}</b> from <b style={{ color: '#fff' }}>{confirmCollect.p.who}</b> ({confirmCollect.method}) — are you sure they paid?
            </p>
            {confirmCollect.tendered != null && confirmCollect.tendered > confirmCollect.amount && (
              <p style={{ fontSize: 13, color: '#FFB300', fontWeight: 800, marginBottom: 4 }}>
                Give ₱{(confirmCollect.tendered - confirmCollect.amount).toLocaleString()} change
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={() => setConfirmCollect(null)} style={{ flex: 1, padding: 11, borderRadius: 8, background: '#1A1A1A', border: '1px solid #2A2A2A', color: '#737373', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', cursor: 'pointer' }}>No</button>
              <button data-testid="org-confirm-collect-btn" disabled={busy}
                onClick={() => doCollect(confirmCollect.p, confirmCollect.amount, confirmCollect.method, confirmCollect.tendered)}
                style={{ flex: 1, padding: 11, borderRadius: 8, background: '#00E676', border: 'none', color: '#000', fontWeight: 900, fontSize: 12, textTransform: 'uppercase', cursor: busy ? 'wait' : 'pointer' }}>
                {busy ? 'Saving…' : 'Yes, paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
